/**
 * The base screens, kept mounted (`D193`).
 *
 * A tab switch used to unmount the screen it left: its scroll offset and its
 * component state were gone, any read not yet cached drew a skeleton on the
 * way back, and the old screen vanished with no exit. Every screen visited
 * since the app started now stays mounted inside React 19's `<Activity>`:
 *
 * - The current screen is `visible`. Every other one is `hidden` — React
 *   keeps its state and its DOM, hides it, and tears its effects down, so a
 *   hidden screen polls nothing, holds no back interceptor, and pauses its
 *   `<Media>` (the clip's own effect cleanup calls `pause()`; nothing ever
 *   re-assigns a `src`, `T-155`). Only the workout screen draws a clip, so
 *   keeping it mounted adds at most one paused player toward the WebView's
 *   cap of forty (`D181`).
 * - `display: none` drops a scroller's offset, so each screen remembers the
 *   offsets of whatever scrolled inside it and puts them back the moment it
 *   is visible again (`ScreenSlot`). The router's own restoration is off
 *   (`router.tsx`): a tab switch is a REPLACE now, a new history key with
 *   nothing cached, and it would fight this.
 * - Moving between screens plays Material 3's patterns on `transform` and
 *   `opacity`, through the Web Animations API, and never blocks a tap: the
 *   screen arriving takes input from its first frame, the one leaving is
 *   `inert` for the few hundred milliseconds it stays visible.
 *     · tab ↔ tab: fade-through — the old screen fades out in 90 ms, the new
 *       one fades and scales in after it.
 *     · into the workout: shared axis X — the workout slides in from the
 *       right over the screen, which slides a little left.
 *     · out of the workout: the same, reversed.
 * - The predictive Back can ask for the screen it would land on to be drawn
 *   under the current one while the gesture is in progress
 *   (`previewScreen`, `app/predictive-back.ts`).
 */
import {
  Activity,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type { Screen } from "./search";

export type ScreenMotion = "fade" | "pop" | "push";

/** How moving from `from` to `to` animates. */
export function screenMotion(from: Screen, to: Screen): ScreenMotion {
  if (to === "workout") {
    return "push";
  }
  if (from === "workout") {
    return "pop";
  }
  return "fade";
}

// ── What the predictive Back reads and writes ───────────────────────────────

interface StackState {
  /** A screen the Back gesture asked to draw under the current one. */
  readonly preview: Screen | null;
  /** The screen shown before the current one, if any. */
  readonly previous: Screen | null;
}

let stackState: StackState = { previous: null, preview: null };
const listeners = new Set<() => void>();

function setStackState(next: StackState): void {
  if (
    next.previous === stackState.previous &&
    next.preview === stackState.preview
  ) {
    return;
  }
  stackState = next;
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The screen shown before the current one — where Back from a pushed screen lands. */
export function previousScreen(): Screen | null {
  return stackState.previous;
}

/** Draws `screen` under the current one (or stops, with `null`). */
export function previewScreen(screen: Screen | null): void {
  setStackState({ ...stackState, preview: screen });
}

let skipEnter = false;

/**
 * The next move plays without its ENTERING half: a committed Back gesture
 * has already slid the screen it lands on into place under the finger.
 */
export function skipNextEnter(): void {
  skipEnter = true;
}

function takeSkipEnter(): boolean {
  const skip = skipEnter;
  skipEnter = false;
  return skip;
}

/** The element a screen is drawn in, for the predictive Back to move. */
export function screenElement(screen: Screen): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-screen="${screen}"]`);
}

/**
 * Scrolls a screen's own scroll region back to the top — the second tap on
 * the tab that is already active, as every native tab bar does.
 */
export function scrollScreenToTop(screen: Screen): void {
  const region = screenElement(screen)?.querySelector<HTMLElement>(
    "[data-scroll-restoration-id], .a-scroll"
  );
  region?.scrollTo({ behavior: "smooth", top: 0 });
}

// ── Motion ──────────────────────────────────────────────────────────────────

const DECELERATE = "cubic-bezier(0.05, 0.7, 0.1, 1)";
const ACCELERATE = "cubic-bezier(0.3, 0, 0.8, 0.15)";
/** Material's fade-through: the old screen is gone before the new one shows. */
const FADE_OUT_MS = 90;
const FADE_IN_MS = 210;
const PUSH_MS = 300;
const POP_MS = 200;
const UNDER_SHIFT = "-6%";
const OVER_SHIFT = "8%";

function animationsOff(): boolean {
  return (
    document.documentElement.hasAttribute("data-shot") ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    typeof Element.prototype.animate !== "function"
  );
}

/**
 * Plays one move. Returns the time until the leaving screen may hide.
 * Nothing here holds a fill: when an animation ends the element is back to
 * its own styles, so a screen hidden and shown later carries no leftover
 * transform.
 */
function play(
  motion: ScreenMotion,
  entering: HTMLElement | null,
  leaving: HTMLElement | null
): number {
  if (motion === "fade") {
    leaving?.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: FADE_OUT_MS,
      easing: ACCELERATE,
      fill: "forwards",
    });
    entering?.animate(
      [
        { opacity: 0, transform: "scale(0.96)" },
        { opacity: 1, transform: "none" },
      ],
      {
        delay: FADE_OUT_MS,
        duration: FADE_IN_MS,
        easing: DECELERATE,
        fill: "backwards",
      }
    );
    return FADE_OUT_MS;
  }
  if (motion === "push") {
    entering?.animate(
      [
        { opacity: 0, transform: `translateX(${OVER_SHIFT})` },
        { opacity: 1, transform: "none" },
      ],
      { duration: PUSH_MS, easing: DECELERATE }
    );
    leaving?.animate(
      [{ transform: "none" }, { transform: `translateX(${UNDER_SHIFT})` }],
      { duration: PUSH_MS, easing: DECELERATE, fill: "forwards" }
    );
    return PUSH_MS;
  }
  leaving?.animate(
    [
      { opacity: 1, transform: "none" },
      { opacity: 0, transform: `translateX(${OVER_SHIFT})` },
    ],
    { duration: POP_MS, easing: ACCELERATE, fill: "forwards" }
  );
  entering?.animate(
    [{ transform: `translateX(${UNDER_SHIFT})` }, { transform: "none" }],
    { duration: PUSH_MS, easing: DECELERATE }
  );
  return POP_MS;
}

/** Cancels every animation an element holds, leftover fills included. */
function settle(element: HTMLElement | null): void {
  for (const animation of element?.getAnimations() ?? []) {
    animation.cancel();
  }
}

// ── The stack ───────────────────────────────────────────────────────────────

interface Leaving {
  readonly motion: ScreenMotion;
  readonly screen: Screen;
}

export interface ScreenStackProps {
  readonly current: Screen;
  /** Draws one screen. Called for every screen kept mounted. */
  readonly render: (screen: Screen) => ReactNode;
}

export function ScreenStack({ current, render }: ScreenStackProps) {
  const [visited, setVisited] = useState<readonly Screen[]>([current]);
  const [shown, setShown] = useState<Screen>(current);
  const [leaving, setLeaving] = useState<Leaving | null>(null);
  const { preview } = useSyncExternalStore(
    subscribe,
    () => stackState,
    () => stackState
  );

  // Derived during render, React's pattern for state that follows a prop:
  // the screen the URL names joins the stack, and the one it replaced starts
  // leaving, in the same render.
  if (!visited.includes(current)) {
    setVisited([...visited, current]);
  }
  if (shown !== current) {
    setLeaving({ motion: screenMotion(shown, current), screen: shown });
    setShown(current);
  }

  useEffect(() => {
    if (leaving !== null) {
      setStackState({ ...stackState, previous: leaving.screen });
    }
  }, [leaving]);

  const container = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (leaving === null) {
      return;
    }
    const root = container.current;
    const entering = root?.querySelector<HTMLElement>(
      `:scope > [data-screen="${current}"]`
    );
    const left = root?.querySelector<HTMLElement>(
      `:scope > [data-screen="${leaving.screen}"]`
    );
    // A move that interrupts another starts from rest, never from a frame of
    // the move it replaced.
    settle(entering ?? null);
    settle(left ?? null);
    const skip = takeSkipEnter();
    if (animationsOff()) {
      setLeaving(null);
      return;
    }
    const wait = play(
      leaving.motion,
      skip ? null : (entering ?? null),
      left ?? null
    );
    const timer = window.setTimeout(() => {
      settle(left ?? null);
      setLeaving(null);
    }, wait);
    return () => window.clearTimeout(timer);
  }, [leaving, current]);

  return (
    <div className="relative h-full" ref={container}>
      {visited.map((screen) => {
        const isCurrent = screen === current;
        const isLeaving = leaving?.screen === screen;
        const isPreview = preview === screen && !isCurrent;
        return (
          <ScreenSlot
            current={isCurrent}
            key={screen}
            layer={slotLayer(isCurrent, isLeaving, leaving?.motion)}
            screen={screen}
            visible={isCurrent || isLeaving || isPreview}
          >
            {render(screen)}
          </ScreenSlot>
        );
      })}
    </div>
  );
}

/**
 * Which of two visible screens paints on top: the arriving one, except when
 * the workout pops away, where the leaving workout slides off ABOVE the
 * screen it uncovers.
 */
function slotLayer(
  isCurrent: boolean,
  isLeaving: boolean,
  motion: ScreenMotion | undefined
): number {
  if (isLeaving) {
    return motion === "pop" ? 2 : 0;
  }
  return isCurrent ? 1 : 0;
}

interface OffsetMemory {
  /** The capture-phase `scroll` listener: notes the target's offset. */
  readonly remember: (event: Event) => void;
  /** Writes every remembered offset back; forgets scrollers that are gone. */
  readonly restore: () => void;
}

/**
 * A screen's scroll offsets, outside React's view: held in a closure rather
 * than a ref so React Compiler, which refuses a component that mutates what
 * it read from a ref inside an effect (`T-168`), compiles the slot.
 */
function createOffsetMemory(): OffsetMemory {
  const offsets = new Map<Element, number>();
  return {
    remember: (event) => {
      const { target } = event;
      if (target instanceof Element) {
        offsets.set(target, target.scrollTop);
      }
    },
    restore: () => {
      for (const [node, top] of offsets) {
        if (!node.isConnected) {
          offsets.delete(node);
        } else if (node.scrollTop !== top) {
          node.scrollTop = top;
        }
      }
    },
  };
}

function ScreenSlot({
  children,
  current,
  layer,
  screen,
  visible,
}: {
  children: ReactNode;
  current: boolean;
  layer: number;
  screen: Screen;
  visible: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [offsets] = useState(createOffsetMemory);

  // Remember every offset as it changes. Capture phase: `scroll` does not
  // bubble, and the scrollers are deep inside the screen.
  useEffect(() => {
    const element = ref.current;
    if (element === null) {
      return;
    }
    element.addEventListener("scroll", offsets.remember, {
      capture: true,
      passive: true,
    });
    return () =>
      element.removeEventListener("scroll", offsets.remember, {
        capture: true,
      });
  }, [offsets]);

  // Shown again: put each offset back before the first paint. Activity has
  // already un-hidden the children in this same commit.
  useLayoutEffect(() => {
    if (visible) {
      offsets.restore();
    }
  }, [visible, offsets]);

  return (
    <div
      aria-hidden={current ? undefined : true}
      className="absolute inset-0"
      data-screen={screen}
      inert={current ? undefined : true}
      ref={ref}
      style={{ display: visible ? undefined : "none", zIndex: layer }}
    >
      <Activity mode={visible ? "visible" : "hidden"}>{children}</Activity>
    </div>
  );
}
